import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createPayment, PAY_METHOD_OPTIONS } from '../api';
import { useToast } from '../../../context/ToastContext';
import { CategoriaConceptoSelect, useCatalogo } from './ConceptoSelect';
import { todayLocal } from '../../../utils/dates';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

// Los que nacen SOLOS de un cobro de Caja (mensualidad, inscripción, clase suelta, vitrina): capturarlos aquí
// los contaría sin alumna ni inventario. Se cobran en Caja como siempre.
const AUTOMATICOS = ['ing_mensualidad', 'ing_pago_tardio', 'ing_inscripcion', 'ing_suelta', 'ing_ayb', 'ing_merch'];

/**
 * «Registrar ingreso» en Finanzas › Ingresos (Mabel 8-oct-2026, video: «así como lo hiciste en gastos, una opción
 * de dar de alta un ingreso» — renta de salón, eventos, otros ingresos — sin pasar por Caja ni confundir a las
 * cajeras). Es un cobro «Otro» con su renglón del catálogo (`concept_id`): sale en esta pestaña, en Caja ›
 * Ingresos y en Reportes con la misma cifra. Si hay caja abierta, el servidor lo estampa en ella como a
 * cualquier cobro (en efectivo entra al corte).
 */
export default function IngresoForm({ onClose, onCreated }: { onClose: () => void; onCreated: (month: string) => void }) {
  const toast = useToast();
  const { catalogo } = useCatalogo();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [conceptId, setConceptId] = useState<number | null>(null);
  const [method, setMethod] = useState('wire');
  const [payer, setPayer] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const conceptName = (catalogo || []).flatMap((s) => s.categorias.flatMap((c) => c.conceptos))
    .find((k) => k.concept_id === conceptId)?.name || '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Indica un monto válido'); return; }
    if (!conceptId) { toast.error('Elige el concepto del ingreso'); return; }
    setSaving(true);
    try {
      const r = await createPayment({
        type: 'other', method, amount: amt, pay_date: date, concept_id: conceptId,
        // El servidor pide quién paga; si no lo escriben, queda el concepto («Renta salón»).
        payer_name: payer.trim() || conceptName || 'Ingreso',
        assoc_name: notes.trim() ? `${conceptName} · ${notes.trim()}` : conceptName,
      });
      toast.success('Ingreso registrado');
      onCreated((r.pay_date || date).slice(0, 7));
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo registrar el ingreso');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Registrar ingreso</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Importe (MXN)</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          <CategoriaConceptoSelect value={conceptId} onChange={setConceptId} secciones={['ingreso']} excluirKeys={AUTOMATICOS} />

          <div>
            <label className={labelCls}>Forma de pago</label>
            <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
              {PAY_METHOD_OPTIONS.map((o) => (
                <button key={o.v} type="button" onClick={() => setMethod(o.v)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${method === o.v ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {method === 'cash' && <p className="mt-1 text-xs text-content-3">En efectivo entra a la caja abierta, para que el corte cuadre.</p>}
          </div>

          <div>
            <label className={labelCls}>Quién paga (opcional)</label>
            <input value={payer} onChange={(e) => setPayer(e.target.value)} className={inputCls} placeholder="Ej. la academia que renta el salón" />
          </div>

          <div>
            <label className={labelCls}>Observaciones (opcional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Ej. renta del sábado 31, evento de fin de año…" />
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
